/**
 * PspProviderMark — TREATX-IYZICO-P0F6
 * ====================================
 *
 * Shows WHO is collecting the payment, using the provider's own official mark.
 *
 * BRAND SOURCE (official, verifiable): iyzico's documented merchant logo pack,
 * https://docs.iyzico.com/en/add-ons/iyzico-logo-pack → `iyzico-logo-pack.zip`.
 * The four SVGs under `public/brand/iyzico/` are unmodified files from that
 * archive (byte-for-byte; checksums in `public/brand/iyzico/PROVENANCE.md`).
 * They are served from our own origin — no external runtime dependency — and
 * were screened for scripts, event handlers and external references.
 *
 * RULES THIS COMPONENT KEEPS
 *  * Proportions are never altered: height is fixed, width is `auto`, and the
 *    SVG keeps its own `viewBox` (210x31 / 210x29). No filter, no recolour, no
 *    redraw, no stretching.
 *  * Clear space: iyzico's brand guide requires a margin around the mark equal
 *    to the height of the "o" in the logotype. At our 28px render height that
 *    is ~8px, applied as padding on all sides.
 *  * Language: iyzico ships separate TR ("iyzico ile Öde") and EN ("Pay with
 *    iyzico") marks. The UI language picks the matching official file — the
 *    wording is never translated by us.
 *  * Background: the colored mark is for light surfaces, the white mark for
 *    dark ones; both are official variants and the pair swaps on Tailwind's
 *    `dark` class.
 *  * Only for the provider that is ACTUALLY collecting: `provider` comes from
 *    the backend checkout response, so Stripe checkout shows no iyzico mark
 *    (and vice versa). Unknown provider renders nothing.
 *
 * WHAT IS DELIBERATELY NOT CLAIMED: this is a factual statement of who the
 * payment provider is. It is not an endorsement, certification, partnership or
 * security claim, and it asserts nothing about which card schemes are accepted
 * — the provider's own embedded form states that (see the P0-F6 report).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

const IYZICO_MARKS = Object.freeze({
  tr: {
    colored: '/brand/iyzico/iyzico_ile_ode_colored_horizontal.svg',
    white: '/brand/iyzico/iyzico_ile_ode_horizontal_white.svg',
    alt: 'iyzico ile Öde',
  },
  en: {
    colored: '/brand/iyzico/pay_with_iyzico_horizontal_colored.svg',
    white: '/brand/iyzico/pay_with_iyzico_horizontal_white.svg',
    alt: 'Pay with iyzico',
  },
});

export const PspProviderMark = ({ provider }) => {
  const { t, i18n } = useTranslation();
  if (String(provider || '').toLowerCase() !== 'iyzico') return null;

  const lang = String(i18n.language || '').toLowerCase().startsWith('tr') ? 'tr' : 'en';
  const mark = IYZICO_MARKS[lang];

  return (
    <div
      className="flex flex-col items-center gap-1.5"
      data-testid="psp-provider-mark"
      data-provider="iyzico"
    >
      <span className="text-xs text-gray-500" data-testid="psp-provider-label">
        {t('upgrade.pspProviderLabel', 'Payment provider')}
      </span>
      {/* p-2 = the brand guide's clear-space margin at this render height. */}
      <span className="inline-flex p-2">
        <img
          src={mark.colored}
          alt={mark.alt}
          height="28"
          className="h-7 w-auto max-w-full block dark:hidden"
          data-testid="psp-provider-mark-img"
        />
        <img
          src={mark.white}
          alt={mark.alt}
          height="28"
          className="h-7 w-auto max-w-full hidden dark:block"
          data-testid="psp-provider-mark-img-dark"
          aria-hidden="true"
        />
      </span>
    </div>
  );
};

export default PspProviderMark;
